import React, { Fragment, memo, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/src/components/ui/accordion";
import { Badge } from "@/src/components/ui/badge";
import { cn } from "@/src/lib/utils";

import { countBulkFindings, flattenBulkObjectFindings } from "./bulkFindings";
import { getBulkObjectGroup, getBulkObjectLabel } from "./bulkObjects";
import BulkValidationFindings from "./BulkValidationFindings";
import { CASTINGS_ROUTE, CONTENTS_ROUTE } from "../routes/routes";

import type { VariantProps } from "class-variance-authority";
import type { JSX } from "react";

import type { badgeVariants } from "@/src/components/ui/badge";

import type { BulkObjectGroup } from "./bulkObjects";
import type {
  BulkFindingSeverity,
  BulkObject,
  BulkValidationSummary,
} from "../../api/BulkAPI.types";

type BadgeVariant = VariantProps<typeof badgeVariants>["variant"];

const GROUP_VARIANT: Record<BulkObjectGroup, BadgeVariant> = {
  detected: "neutral",
  inserted: "success",
  insertedWithWarnings: "warning",
  failed: "error",
  duplicate: "secondary",
};

interface BulkObjectRowProps {
  object: BulkObject;
}

const FINDING_COUNT_CLASS_NAME: Record<BulkFindingSeverity, string> = {
  error: "text-status-error",
  warning: "text-status-warning",
};

interface BulkFindingCountProps {
  label: string;
  severity: BulkFindingSeverity;
}

interface BulkObjectRowSummaryProps {
  counts: BulkValidationSummary;
  group: BulkObjectGroup;
  object: BulkObject;
}

function getBulkObjectLink(object: BulkObject): string | null {
  if (object.okast_uuid === null) {
    return null;
  }

  if (object.type === "media" && "storage" in object.data) {
    return `${CONTENTS_ROUTE}/${object.data.storage}/${object.okast_uuid}`;
  }

  if (object.type === "casting") {
    return `${CASTINGS_ROUTE}/${object.okast_uuid}`;
  }

  return null;
}

function BulkFindingCount({
  label,
  severity,
}: BulkFindingCountProps): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs",
        FINDING_COUNT_CLASS_NAME[severity],
      )}
    >
      <span
        aria-hidden="true"
        className="size-2 shrink-0 rounded-full bg-current"
      />
      {label}
    </span>
  );
}

function BulkObjectRowSummary({
  counts,
  group,
  object,
}: BulkObjectRowSummaryProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");

  return (
    <Fragment>
      <Badge className="shrink-0" variant="outline">
        {t(`objectType.${object.type}`, {
          defaultValue: t("objectType.unknown"),
        })}
      </Badge>

      <span className="min-w-0 flex-1 truncate text-left text-sm">
        {getBulkObjectLabel(object)}
      </span>

      {counts.errors > 0 ? (
        <BulkFindingCount
          label={t("errorsCount", { count: counts.errors })}
          severity="error"
        />
      ) : null}

      {counts.warnings > 0 ? (
        <BulkFindingCount
          label={t("warningsCount", { count: counts.warnings })}
          severity="warning"
        />
      ) : null}

      <Badge variant={GROUP_VARIANT[group]}>{t(`objectGroup.${group}`)}</Badge>
    </Fragment>
  );
}

function BulkObjectRow({ object }: BulkObjectRowProps): JSX.Element {
  const { t } = useTranslation("editor-bulk");

  const group = getBulkObjectGroup(object);
  const findingList = useMemo(
    () => flattenBulkObjectFindings(object),
    [object],
  );
  const link = getBulkObjectLink(object);
  const linkLabelKey =
    object.type === "casting" ? "goto-casting" : "goto-media";

  const hasFindings = findingList.length > 0;
  const hasDuplicateMessage = group === "duplicate";
  const hasFarmStatus =
    object.type === "media" &&
    (group === "inserted" || group === "insertedWithWarnings");
  const hasLink = link !== null;
  const hasDetails =
    hasFindings || hasDuplicateMessage || hasFarmStatus || hasLink;

  const summary = (
    <BulkObjectRowSummary
      counts={countBulkFindings(findingList)}
      group={group}
      object={object}
    />
  );

  if (!hasDetails) {
    return (
      <div className="flex items-center justify-between gap-3 border-b px-4 py-4 text-left text-sm font-medium last:border-b-0">
        {summary}
        <span aria-hidden="true" className="size-4 shrink-0" />
      </div>
    );
  }

  return (
    <AccordionItem value={object.uuid}>
      <AccordionTrigger className="cursor-pointer items-center gap-3 px-4 hover:no-underline [&>svg]:translate-y-0">
        {summary}
      </AccordionTrigger>

      <AccordionContent className="flex flex-col gap-3 px-4">
        {hasFindings ? (
          <BulkValidationFindings findingList={findingList} />
        ) : null}

        {hasDuplicateMessage ? (
          <p className="text-sm">{t("duplicate-message")}</p>
        ) : null}

        {hasFarmStatus ? (
          <p className="text-muted-foreground text-xs">
            {t("farmStatus.label")} : {t(`farmStatus.${object.farm_status}`)}
          </p>
        ) : null}

        {hasLink ? (
          <Link className="text-primary text-sm" target="_blank" to={link}>
            {t(linkLabelKey)}
          </Link>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  );
}

export default memo(BulkObjectRow);
